import { useState } from "react";
import { toast } from "sonner";
import { Home as HomeIcon, Mail, Phone, Loader2, Check } from "lucide-react";
import { createCheckout } from "../lib/api";
import { NewHomeProgress } from "../components/NewHomeProgress";
import { OpeningCountdown } from "../components/OpeningCountdown";
import { RibbonCuttingRsvp } from "../components/RibbonCuttingRsvp";

const LEVELS = [
  { key: "new_home_founding", title: "Founding Home Sponsor", amount: 16500, note: "The full first year of rent — name on our sponsor wall & entry signage", featured: true },
  { key: "new_home_partner", title: "Home Partner", amount: 8250, note: "Six months of rent — sponsor wall & ribbon-cutting recognition" },
  { key: "new_home_month", title: "Month Sponsor", amount: 1375, note: "One month of rent — our Rent Sponsor of the Month" },
];

function LevelCard({ level }) {
  const [donor, setDonor] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);

  const pay = async () => {
    setLoading(true);
    try {
      const { checkout_url } = await createCheckout({
        lookup_key: level.key,
        amount: level.amount,
        origin_url: window.location.origin,
        donor_name: donor.name || undefined,
        donor_email: donor.email || undefined,
        designation: "new-home",
      });
      window.location.href = checkout_url;
    } catch {
      toast.error("We couldn't start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className={`rounded-xl p-7 flex flex-col gap-5 border ${level.featured ? "bg-[#1A0B2E] text-white border-[#F5B90A]" : "bg-white border-black/10"}`} data-testid={`new-home-level-${level.key}`}>
      <div>
        <p className={`overline ${level.featured ? "text-[#F5B90A]" : "text-[#6B21A8]"}`}>{level.featured ? "Biggest impact" : "Sponsor level"}</p>
        <h3 className="mt-2 font-display text-2xl font-bold">{level.title}</h3>
        <p className="mt-1 font-display text-4xl font-bold text-[#F5B90A]" data-testid={`new-home-amount-${level.key}`}>${level.amount.toLocaleString()}</p>
        <p className={`mt-3 text-sm ${level.featured ? "text-white/75" : "text-black/65"}`}>{level.note}</p>
      </div>
      <div className="grid gap-2 mt-auto">
        <input className="rounded-md border border-black/15 px-3 py-2 text-sm text-black" placeholder="Your name or business (for recognition)" value={donor.name} onChange={(e) => setDonor({ ...donor, name: e.target.value })} data-testid={`new-home-name-${level.key}`} />
        <input type="email" className="rounded-md border border-black/15 px-3 py-2 text-sm text-black" placeholder="Email for receipt" value={donor.email} onChange={(e) => setDonor({ ...donor, email: e.target.value })} data-testid={`new-home-email-${level.key}`} />
        <button onClick={pay} disabled={loading} className="btn-gold w-full justify-center" data-testid={`new-home-pay-${level.key}`}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <HomeIcon className="h-4 w-4" />} Sponsor online
        </button>
      </div>
    </div>
  );
}

export default function NewHome() {
  return (
    <>
      <section className="relative overflow-hidden bg-[#1A0B2E] text-white" data-testid="new-home-hero">
        <div className="noise" />
        <div className="container-x relative py-20 md:py-28 grid gap-12 md:grid-cols-12 items-center">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-line" />
              <span className="overline text-[#F5B90A]">Fundraiser · Opening December 1, 2026</span>
            </div>
            <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1.02]">
              Stand behind <span className="text-[#F5B90A]">our new home.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-white/80">
              On December 1, 2026, we open the doors of our new mentoring center right here in Forest Park. Help us cover
              rent so every dollar of programming goes straight to our young men.
            </p>
            <ul className="mt-6 space-y-2 text-white/75 text-sm">
              {["501(c)(3) nonprofit", "Gifts are tax-deductible", "Monthly installments welcome — contact us to arrange"].map((t) => (
                <li key={t} className="flex items-center gap-2"><Check className="h-4 w-4 text-[#F5B90A]" /> {t}</li>
              ))}
            </ul>
            <a href="#levels" className="btn-gold mt-8 inline-flex" data-testid="new-home-cta">See sponsor levels</a>
            <div className="mt-10 max-w-xl">
              <p className="overline text-white/60 mb-3">Countdown to opening day</p>
              <OpeningCountdown />
            </div>
            <div className="mt-8 max-w-xl">
              <NewHomeProgress />
            </div>
          </div>
          <div className="md:col-span-5">
            <img src="/photos/new-home-flyer.jpg" alt="Stand Behind Our New Home fundraiser flyer" className="w-full rounded-xl ring-1 ring-[#F5B90A]/40 shadow-[0_30px_80px_-20px_rgba(107,33,168,0.7)]" data-testid="new-home-flyer" />
          </div>
        </div>
      </section>

      <section id="levels" className="bg-[#F7F3EC] py-20 md:py-24" data-testid="new-home-levels">
        <div className="container-x">
          <span className="overline text-[#6B21A8]">Three ways to stand behind the home</span>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold">Choose your sponsor level.</h2>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {LEVELS.map((l) => <LevelCard key={l.key} level={l} />)}
          </div>
        </div>
      </section>

      <RibbonCuttingRsvp />

      <section className="bg-white py-16" data-testid="new-home-contact">
        <div className="container-x text-center max-w-2xl mx-auto">
          <h2 className="font-display text-3xl md:text-4xl font-bold">Prefer to talk first or pay in installments?</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href="mailto:change@changecentermentoring.com?subject=New%20Home%20Sponsorship" className="btn-primary" data-testid="new-home-email-btn"><Mail className="h-4 w-4" /> change@changecentermentoring.com</a>
            <a href="tel:+14043260944" className="btn-outline" data-testid="new-home-phone-btn"><Phone className="h-4 w-4" /> (404) 326-0944</a>
          </div>
          <p className="mt-10 font-display italic text-lg text-[#6B21A8]">"Changing this generation one young man at a time."</p>
          <p className="text-sm text-black/60">Duane Thomas Jr. · Founder & CEO</p>
        </div>
      </section>
    </>
  );
}
